'use client';

import { FormWithAction } from '@/general/components/forms/FormWithAction/FormWithAction';
import { getOr } from '@/utils/getOr';
import { useRouter } from 'next/navigation';
import { FC, useCallback, useState } from 'react';

import { Button } from 'src/general/components/Button/Button';
import { Image } from 'src/general/components/Image/Image';
import { RecaptchaNotice } from 'src/general/components/RecaptchaNotice/RecaptchaNotice';
import { tradeInSearchCarRegoAction } from 'src/hygiene/components/TradeInEnquiry/TradeInSearchCar/TradeInSearchCar.action';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { TradeInBannerFields } from './TradeInBannerFields';

interface Props {
  sku: string;
}

export const TradeInBanner: FC<Props> = ({ sku }) => {
  const [userInteractedWithForm, setUserInteractedWithForm] = useState(false);
  const router = useRouter();

  const onInteract = useCallback(() => {
    if (!userInteractedWithForm) {
      pushToDataLayer({
        event: 'pdp_interaction',
        type: 'text_box',
        value: 'trade_in',
      });
      setUserInteractedWithForm(true);
    }
  }, [userInteractedWithForm, sku]);

  return (
    <section className="mb-16 rounded-lg border border-bor-neu-mut bg-bac-tie1 p-4 shadow-elevation-0dp">
      <div className="flex flex-col gap-4 smp:flex-row">
        <header className="flex gap-2 md:gap-4">
          <Image
            src="/images/components/BadgeIllustration/trade-in.svg"
            width={56}
            height={56}
            alt=""
            className="size-14"
          />
          <div>
            <p className="c-fs-body1 font-extrabold">Trade and upgrade</p>
            <p className="c-fs-caption">
              Unlock the value of your old car. Get a quick quote today and upgrade to your dream car.
            </p>
          </div>
        </header>
        <div className="flex flex-col gap-2">
          <FormWithAction
            action={tradeInSearchCarRegoAction}
            onSuccess={(success) => {
              pushToDataLayer({ event: 'pdp_trade_in_start' });
              router.push(success.message);
            }}
            renderContent={(state) => (
              <>
                <div className="flex w-full flex-col items-center justify-center gap-2 sm:flex-row">
                  <div className="relative flex h-14.5 w-full gap-2 rounded-xs border-3 border-whi-800 px-4 font-extrabold shadow-elevation-2dp sm:w-72">
                    <TradeInBannerFields loading={false} onInteract={onInteract} />
                    <Image
                      src="/images/components/TradeIn/Screw.svg"
                      className="pointer-events-none absolute top-1 left-1"
                      alt=""
                      width={7}
                      height={7}
                    />
                    <Image
                      src="/images/components/TradeIn/Screw.svg"
                      className="pointer-events-none absolute top-1 right-1"
                      alt=""
                      width={7}
                      height={7}
                    />
                    <Image
                      src="/images/components/TradeIn/Screw.svg"
                      className="pointer-events-none absolute bottom-1 left-1"
                      alt=""
                      width={7}
                      height={7}
                    />
                    <Image
                      src="/images/components/TradeIn/Screw.svg"
                      className="pointer-events-none absolute right-1 bottom-1"
                      alt=""
                      width={7}
                      height={7}
                    />
                  </div>
                  <div className="w-full sm:w-fit">
                    <Button type="submit" data-testid="trade-in-go-button" fullWidth>
                      Go
                    </Button>
                  </div>
                </div>
                {state.status === 'errorValidation' && getOr(state, `errors.rego[0]`, null) && (
                  <div className="c-fs-caption text-center text-tex-err-tie1 sm:pr-18">
                    {getOr(state, `errors.rego[0]`, null) as string | null}
                  </div>
                )}
              </>
            )}
          />
        </div>
      </div>
      <div className="mt-4 text-center">
        <RecaptchaNotice />
      </div>
    </section>
  );
};
